import axios from 'axios';
import { getDeviceType } from '../utils/deviceType';

// --- Global Debug Flag (Development Only) ---
// SECURITY: Debug logging disabled in production to prevent exposing tokens and sensitive data
const DEBUG_API = process.env.NODE_ENV === 'development';

// Create a single, configured instance of axios
// Determine the API URL based on environment
const getApiUrl = () => {
  const envApiUrl = process.env.REACT_APP_API_URL;
  const nodeEnv = process.env.NODE_ENV;

  // CRITICAL: In production, API URL MUST be set
  if (!envApiUrl) {
    if (nodeEnv === 'production') {
      // In production build, fail fast with clear error
      const errorMsg =
        '🚨 CRITICAL: REACT_APP_API_URL environment variable is not set!\n' +
        'Production builds MUST have API URL configured.\n' +
        'Set REACT_APP_API_URL in your build environment.';
      console.error(errorMsg);
      throw new Error(errorMsg);
    }

    // Development: Allow fallback with warning
    console.warn('⚠️ REACT_APP_API_URL not set, falling back to localhost:8000 (DEV ONLY)');
    return 'http://localhost:8000/api';
  }

  return envApiUrl;
};

const API_BASE_URL = getApiUrl();

// CRITICAL: Production must use HTTPS
if (process.env.NODE_ENV === 'production') {
  if (!API_BASE_URL.startsWith('https://')) {
    const errorMsg =
      `🚨 CRITICAL: Production API URL must use HTTPS!\n` +
      `Current URL: ${API_BASE_URL}\n` +
      `Update REACT_APP_API_URL to use https://`;
    console.error(errorMsg);
    throw new Error(errorMsg);
  }
}

console.log('🔌 API Base URL:', API_BASE_URL);
console.log('🌍 Environment:', process.env.NODE_ENV);

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate axios instance for admin tests (password-protected)
const adminTestsApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Request Interceptor (Attach Token & Debug) ---
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // ✅ Use 'Token', not 'Bearer', for Django's TokenAuthentication
      config.headers.Authorization = `Token ${token}`;
    }
    // Device hint for User Intelligence analytics (mobile vs desktop usage)
    config.headers['X-Device-Type'] = getDeviceType();
    if (DEBUG_API) {
      console.log(`🚀 [API REQUEST] ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data || {},
        params: config.params || {},
      });
    }
    return config;
  },
  (error) => {
    DEBUG_API && console.error('❌ [API REQUEST ERROR]', error);
    return Promise.reject(error);
  }
);

// --- Response Interceptor (Handle 401 Unauthorized & Debug) ---
apiClient.interceptors.response.use(
  (response) => {
    if (DEBUG_API) {
      console.log(`✅ [API RESPONSE] ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }
    return response;
  },
  (error) => {
    if (DEBUG_API) {
      console.error('❌ [API RESPONSE ERROR]', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
      });
    }
    if (error.response && error.response.status === 401) {
      // Token invalid or expired → log out & redirect
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// --- Admin Tests API Client Interceptors (Password-based auth + optional token) ---
adminTestsApiClient.interceptors.request.use(
  (config) => {
    const password = localStorage.getItem('adminTestsPassword');
    if (password) {
      config.headers['X-Admin-Password'] = password;
    }
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    if (DEBUG_API) {
      console.log(`🧪 [ADMIN TEST REQUEST] ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data || {},
        params: config.params || {},
      });
    }
    return config;
  },
  (error) => {
    DEBUG_API && console.error('❌ [ADMIN TEST REQUEST ERROR]', error);
    return Promise.reject(error);
  }
);

adminTestsApiClient.interceptors.response.use(
  (response) => {
    if (DEBUG_API) {
      console.log(`✅ [ADMIN TEST RESPONSE] ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }
    return response;
  },
  (error) => {
    if (DEBUG_API) {
      console.error('❌ [ADMIN TEST RESPONSE ERROR]', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
      });
    }
    if (error.response && error.response.status === 401) {
      const detail = error.response.data?.detail || '';
      if (detail === 'Invalid token.') {
        // Stale DRF token → clear it and send the user to log in fresh
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        // Password incorrect or missing → clear password and trigger re-prompt
        localStorage.removeItem('adminTestsPassword');
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// --- Authentication Functions ---
export const login = async (username, password) => {
  DEBUG_API && console.log('🔑 [API] Login attempt:', { username });
  try {
    const response = await apiClient.post('/auth/login/', { username, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      return { error: error.response.data.error };
    } else if (error.response?.status === 401) {
      return { error: 'Invalid username or password. Please try again.' };
    } else if (error.response?.status === 400) {
      return { error: 'Username and password are required.' };
    }
    return { error: 'Unable to connect to server. Please try again later.' };
  }
};

export const signup = async (username, password, email) => {
  DEBUG_API && console.log('📝 [API] Signup attempt:', { username, email });
  try {
    const response = await apiClient.post('/auth/signup/', {
      username,
      password,
      email
    });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      return { error: error.response.data.error };
    } else if (error.response?.status === 400) {
      return { error: 'Username already exists or invalid input.' };
    } else if (error.response?.status === 404) {
      return { error: 'Cannot connect to server. Is the backend running?' };
    } else if (!error.response) {
      return { error: 'Network error. Check if backend is running and CORS is configured.' };
    }
    return { error: 'Unable to create account. Please try again.' };
  }
};

export const logout = () => {
  DEBUG_API && console.log('🚪 [API] Logging out...');
  localStorage.removeItem('token');
};

/**
 * Authenticate with Google OAuth
 * @param {string} credential - Google ID token from Google Sign-In
 * @returns {Object} Response with DRF token, username, email, and is_new_user flag
 */
export const googleAuth = async (credential) => {
  DEBUG_API && console.log('🔐 [API] Google authentication attempt');
  try {
    const response = await apiClient.post('/auth/google-auth/', { credential });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      return { error: error.response.data.error };
    } else if (error.response?.status === 401) {
      return { error: 'Google authentication failed. Please try again.' };
    } else if (error.response?.status === 503) {
      return { error: 'Google authentication is not available at this time.' };
    }
    return { error: 'Unable to authenticate with Google. Please try again later.' };
  }
};

// --- Screener ---
export const getOptions = () => {
  DEBUG_API && console.log('📡 [API] Fetching options...');
  return apiClient.get('/contracts/options/');
};
export const getScreener = () => apiClient.get('/contracts/screener/');
export const runScreenerBackend = (data) => {
  DEBUG_API && console.log('📡 [API] Running screener backend:', data);
  return apiClient.post('/run-screener/', data); // ✅ Correct endpoint
};

/**
 * Recalculate contract with new simulation parameters (mobile stories sliders)
 * Uses Polygon.io as primary data source, EODHD as automatic fallback
 * @param {Object} data - Contract parameters
 * @param {string} data.ticker - Stock ticker symbol
 * @param {string} data.option_type - 'call' or 'put'
 * @param {number} data.strike - Strike price
 * @param {string} data.expiration - Expiration date (YYYY-MM-DD)
 * @param {number} data.days_to_gain - Days to target date
 * @param {number} data.stock_gain_pct - Stock movement percentage (e.g., 0.15 for 15%)
 * @returns {Promise} Response with recalculated premium and gain
 */
export const recalculateContract = (data) => {
  DEBUG_API && console.log('🔄 [API] Recalculating contract:', data);
  return apiClient.post('/recalculate-contract/', data);
};

// --- Scoring Presets (Custom Weighted Scoring Builder) ---
// Routes live under /stock-analyzer/ in the API surface to match the UI
// location (/stock-analyzer/scoring-builder). The Django views still live in
// research/views.py — the stock_analyzer app's urls.py imports them.
export const getScoringPresets = () => apiClient.get('/stock-analyzer/scoring-presets/');
export const getScoringPreset = (id) => apiClient.get(`/stock-analyzer/scoring-presets/${id}/`);
export const saveScoringPreset = (data) => {
  DEBUG_API && console.log('💾 [API] Saving scoring preset:', data?.name);
  return apiClient.post('/stock-analyzer/scoring-presets/', data);
};
export const updateScoringPreset = (id, data) => {
  DEBUG_API && console.log(`✏️ [API] Updating scoring preset ${id}`);
  return apiClient.put(`/stock-analyzer/scoring-presets/${id}/`, data);
};
export const deleteScoringPreset = (id) => {
  DEBUG_API && console.log(`🗑 [API] Deleting scoring preset ${id}`);
  return apiClient.delete(`/stock-analyzer/scoring-presets/${id}/`);
};
export const setDefaultScoringPreset = (id) =>
  apiClient.patch(`/stock-analyzer/scoring-presets/${id}/set-default/`);
export const clearDefaultScoringPreset = (id) =>
  apiClient.delete(`/stock-analyzer/scoring-presets/${id}/set-default/`);

// --- Ticker Groups (favorite groupings used by Scoring Builder) ---
export const getTickerGroups = () => apiClient.get('/stock-analyzer/ticker-groups/');
export const saveTickerGroup = (data) =>
  apiClient.post('/stock-analyzer/ticker-groups/', data);
export const updateTickerGroup = (id, data) =>
  apiClient.put(`/stock-analyzer/ticker-groups/${id}/`, data);
export const deleteTickerGroup = (id) =>
  apiClient.delete(`/stock-analyzer/ticker-groups/${id}/`);

// --- Saved Screener Parameters ---
export const getSavedParameters = () => apiClient.get('/saved-parameters/'); // ✅ Correct endpoint
export const saveParameterSet = (data) => {
  DEBUG_API && console.log('💾 [API] Saving parameter set:', data);
  return apiClient.post('/saved-parameters/', data); // ✅ Correct endpoint
};
export const deleteParameterSet = (id) => {
  DEBUG_API && console.log(`🗑 [API] Deleting parameter set ID: ${id}`);
  return apiClient.delete(`/saved-parameters/${id}/`); // ✅ Correct endpoint
};

// --- Saved Contracts ---
export const getSavedContracts = () => {
  DEBUG_API && console.log('📡 [API] Fetching saved contracts...');
  return apiClient.get('/saved-contracts/');
};
export const saveContract = (data) => {
  DEBUG_API && console.log('💾 [API] Saving contract:', data);
  return apiClient.post('/saved-contracts/', data);
};
export const deleteSavedContract = (id) => {
  DEBUG_API && console.log(`🗑 [API] Deleting contract ID: ${id}`);
  return apiClient.delete(`/saved-contracts/${id}/`);
};
export const resetDaysToGain = (id) => apiClient.patch(`/saved-contracts/${id}/reset-days/`);
export const refreshContractData = (id) => apiClient.patch(`/saved-contracts/${id}/refresh/`);

// --- Watchlist Groups ---
export const getWatchlistGroups = () => {
  DEBUG_API && console.log('📡 [API] Fetching watchlist groups...');
  return apiClient.get('/watchlist-groups/');
};
export const createWatchlistGroup = (data) => {
  DEBUG_API && console.log('➕ [API] Creating watchlist group:', data);
  return apiClient.post('/watchlist-groups/', data);
};
export const updateWatchlistGroup = (id, data) => {
  DEBUG_API && console.log(`✏ [API] Updating watchlist group ID: ${id}`, data);
  return apiClient.put(`/watchlist-groups/${id}/`, data);
};
export const deleteWatchlistGroup = (id) => {
  DEBUG_API && console.log(`🗑 [API] Deleting watchlist group ID: ${id}`);
  return apiClient.delete(`/watchlist-groups/${id}/`);
};
export const assignContractsToGroup = (groupIds, contractIds, mode = 'append') => {
  DEBUG_API && console.log('🔗 [API] Assigning contracts to groups:', { groupIds, contractIds, mode });
  const requests = groupIds.map((groupId) =>
    apiClient.post(`/watchlist-groups/${groupId}/assign-contracts/`, {
      contract_ids: contractIds,
      mode,
    })
  );
  return Promise.all(requests);
};

// --- Simulate Watchlist Group ---
export const simulateGroupContracts = (groupId) => {
  DEBUG_API && console.log(`🧪 [API] Simulating group ID: ${groupId}`);
  return apiClient.post(`/watchlist-groups/${groupId}/simulate/`);
};

// --- Refresh Watchlist Group ---
export const refreshGroupContracts = (groupId) => {
  DEBUG_API && console.log(`🔄 [API] Refreshing group ID: ${groupId}`);
  return apiClient.post(`/watchlist-groups/${groupId}/refresh/`);
};

// --- Job Status Tracking ---
export const getJobStatus = (jobId) => {
  DEBUG_API && console.log(`📊 [API] Getting job status for ID: ${jobId}`);
  return apiClient.get(`/jobs/${jobId}/`);
};

// --- Bulk Watchlist Simulation ---
export const runBulkWatchlist = (data) => {
  DEBUG_API && console.log('🧪 [API] Running bulk watchlist simulation:', data);
  return apiClient.post('/run-bulk-watchlist/', data);
};

// --- Get Options Expiration Dates ---
export const getOptionsExpirationDates = async (ticker) => {
  DEBUG_API && console.log(`📅 [API] Fetching expiration dates for ticker: ${ticker}`);
  try {
    const response = await apiClient.get(`/options/expiration-dates/?ticker=${ticker}`);
    return response.data;
  } catch (error) {
    console.error(`❌ [API] Failed to fetch expiration dates for ${ticker}:`, error);
    throw error;
  }
};

// --- Get Options Strike Prices ---
export const getOptionsStrikePrices = async (ticker, optionType, expiration) => {
  DEBUG_API && console.log(`📊 [API] Fetching strike prices for: ${ticker} ${optionType} ${expiration}`);
  try {
    const response = await apiClient.get(
      `/options/strike-prices/?ticker=${ticker}&option_type=${optionType}&expiration=${expiration}`
    );
    return response.data;
  } catch (error) {
    console.error(`❌ [API] Failed to fetch strike prices:`, error);
    throw error;
  }
};

// --- Admin Testing Functions ---
export const runAdminTests = (environment, testTypes) => {
  // Use the same API URL configured for all other API calls
  // This ensures tests go to the backend (api.optionsleveling.com), not frontend (app.optionsleveling.com)
  const targetUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  DEBUG_API && console.log('🧪 [API] Running admin tests:', { environment, testTypes, targetUrl });
  return adminTestsApiClient.post('/admin-tests/run/', {
    environment,
    test_types: testTypes,
    target_url: targetUrl
  }).then(response => response.data);
};

export const getTestRuns = () => {
  DEBUG_API && console.log('📋 [API] Fetching test runs');
  return adminTestsApiClient.get('/admin-tests/runs/').then(response => response.data);
};

export const getTestRunDetail = (runId) => {
  DEBUG_API && console.log('🔍 [API] Fetching test run detail:', runId);
  return adminTestsApiClient.get(`/admin-tests/runs/${runId}/`).then(response => response.data);
};

export const getTestRunStatus = (runId) => {
  DEBUG_API && console.log('📊 [API] Checking test run status:', runId);
  return adminTestsApiClient.get(`/admin-tests/runs/${runId}/status/`).then(response => response.data);
};

export const getAdminTestHealth = () => {
  DEBUG_API && console.log('❤️ [API] Checking admin test health');
  return adminTestsApiClient.get('/admin-tests/health/').then(response => response.data);
};

// --- Admin Reports Functions ---
export const getReportsUserSummary = async () => {
  DEBUG_API && console.log('📊 [API] Fetching reports user summary...');
  try {
    const response = await adminTestsApiClient.get('/admin-tests/reports/user-summary/');
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch user summary:', error);
    throw error;
  }
};

export const getReportsToolUsage = async (period = '7d') => {
  DEBUG_API && console.log(`📊 [API] Fetching reports tool usage (${period})...`);
  try {
    const response = await adminTestsApiClient.get(`/admin-tests/reports/tool-usage/?period=${period}`);
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch tool usage:', error);
    throw error;
  }
};

export const getReportsUserLookup = async (username, period = '30d') => {
  DEBUG_API && console.log(`📊 [API] Fetching user lookup for ${username} (${period})...`);
  try {
    const response = await adminTestsApiClient.get(
      `/admin-tests/reports/user-lookup/?username=${encodeURIComponent(username)}&period=${period}`
    );
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch user lookup:', error);
    throw error;
  }
};

// --- Analytics Dashboard API Functions ---

export const getActiveUsersTimeSeries = async (days = 90) => {
  DEBUG_API && console.log(`[API] Fetching active users time series (${days} days)...`);
  try {
    const response = await adminTestsApiClient.get(`/admin-tests/reports/active-users/?days=${days}`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch active users:', error);
    throw error;
  }
};

export const getFeatureAdoption = async (period = '30d') => {
  DEBUG_API && console.log(`[API] Fetching feature adoption (${period})...`);
  try {
    const response = await adminTestsApiClient.get(`/admin-tests/reports/feature-adoption/?period=${period}`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch feature adoption:', error);
    throw error;
  }
};

export const getConversionFunnel = async (period = '90d') => {
  DEBUG_API && console.log(`[API] Fetching conversion funnel (${period})...`);
  try {
    const response = await adminTestsApiClient.get(`/admin-tests/reports/conversion-funnel/?period=${period}`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch conversion funnel:', error);
    throw error;
  }
};

export const getUserJourney = async (username, days = 30) => {
  DEBUG_API && console.log(`[API] Fetching user journey for ${username} (${days} days)...`);
  try {
    const response = await adminTestsApiClient.get(
      `/admin-tests/reports/user-journey/?username=${encodeURIComponent(username)}&days=${days}`
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch user journey:', error);
    throw error;
  }
};

export const getRetentionCohorts = async (weeks = 12) => {
  DEBUG_API && console.log(`[API] Fetching retention cohorts (${weeks} weeks)...`);
  try {
    const response = await adminTestsApiClient.get(`/admin-tests/reports/retention/?weeks=${weeks}`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch retention cohorts:', error);
    throw error;
  }
};

export const getUserIntelligence = async () => {
  DEBUG_API && console.log('[API] Fetching user intelligence data...');
  try {
    const response = await adminTestsApiClient.get('/admin-tests/reports/user-intelligence/');
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch user intelligence:', error);
    throw error;
  }
};

// --- Admin Account Deletion Management ---

export const getPendingDeletions = async () => {
  DEBUG_API && console.log('[API] Fetching pending account deletions...');
  try {
    const response = await adminTestsApiClient.get('/admin-tests/reports/pending-deletions/');
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch pending deletions:', error);
    throw error;
  }
};

export const adminReinstateAccount = async (userId) => {
  DEBUG_API && console.log(`[API] Admin reinstating account for user ${userId}...`);
  try {
    const response = await adminTestsApiClient.post('/admin-tests/reports/reinstate-account/', {
      user_id: userId
    });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to reinstate account:', error);
    throw error;
  }
};

// --- Admin Test User Cleanup ---

export const getTestUsers = async () => {
  DEBUG_API && console.log('[API] Fetching test users...');
  try {
    const response = await adminTestsApiClient.get('/admin-tests/data/test-users/');
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch test users:', error);
    throw error;
  }
};

export const deleteTestUsers = async (userIds) => {
  DEBUG_API && console.log(`[API] Deleting ${userIds.length} test users...`);
  try {
    const response = await adminTestsApiClient.post('/admin-tests/data/delete-test-users/', {
      user_ids: userIds
    });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to delete test users:', error);
    throw error;
  }
};

// Tutorial Video admin CRUD (uses admin password auth)
export const getTutorialVideosAdmin = () =>
  adminTestsApiClient.get('/admin-tests/tutorial-videos/').then(r => r.data);

export const createTutorialVideo = (data) =>
  adminTestsApiClient.post('/admin-tests/tutorial-videos/', data);

export const updateTutorialVideo = (id, data) =>
  adminTestsApiClient.put(`/admin-tests/tutorial-videos/${id}/`, data);

export const deleteTutorialVideo = (id) =>
  adminTestsApiClient.delete(`/admin-tests/tutorial-videos/${id}/`);

// Export admin tests client for DataLoading component
export const adminTestsClient = adminTestsApiClient;

export default apiClient;

// --- Research Module ---
export const runResearchFundamentals = (tickers) => {
  DEBUG_API && console.log('[API] Running research fundamentals:', tickers);
  return apiClient.post('/research/fundamentals/', { tickers });
};

export const runResearchFinancials = (tickers, years = 5) => {
  DEBUG_API && console.log('[API] Running research financials:', tickers, years);
  return apiClient.post('/research/financials/ratios/', { tickers, years });
};

export const runResearchEpsTrends = (tickers) => {
  DEBUG_API && console.log('[API] Running research EPS trends:', tickers);
  return apiClient.post('/research/eps-trends/', { tickers });
};

/**
 * Log a Research page/tab view for User Intelligence analytics.
 * Fire-and-forget: failures are swallowed so analytics never disrupt the UI.
 * @param {string} page - one of 'input' | 'fundamentals' | 'financials' | 'epsTrends'
 */
export const logResearchPageView = (page) => {
  return apiClient.post('/research/page-view/', { page }).catch((err) => {
    DEBUG_API && console.warn('[API] research page-view log failed:', err?.message);
  });
};

// --- Dashboard Builder ---
export const getDashboards = () => {
  DEBUG_API && console.log('📊 [API] Fetching dashboards...');
  return apiClient.get('/dashboard-builder/dashboards/');
};

export const createDashboard = (data) => {
  DEBUG_API && console.log('✨ [API] Creating dashboard:', data);
  return apiClient.post('/dashboard-builder/dashboards/', data);
};

export const updateDashboard = (id, data) => {
  DEBUG_API && console.log(`✏️ [API] Updating dashboard ID ${id}:`, data);
  return apiClient.put(`/dashboard-builder/dashboards/${id}/`, data);
};

export const deleteDashboard = (id) => {
  DEBUG_API && console.log(`🗑 [API] Deleting dashboard ID: ${id}`);
  return apiClient.delete(`/dashboard-builder/dashboards/${id}/`);
};

export const createWidget = (dashboardId, data) => {
  DEBUG_API && console.log(`📈 [API] Creating widget for dashboard ${dashboardId}:`, data);
  return apiClient.post(`/dashboard-builder/dashboards/${dashboardId}/widgets/`, data);
};

export const deleteWidget = (id) => {
  DEBUG_API && console.log(`🗑 [API] Deleting widget ID: ${id}`);
  return apiClient.delete(`/dashboard-builder/widgets/${id}/`);
};

export const updateWidget = (id, data) => {
  DEBUG_API && console.log(`✏️ [API] Updating widget ID ${id}:`, data);
  return apiClient.put(`/dashboard-builder/widgets/${id}/`, data);
};

export const bulkUpdateWidgetPositions = (positions) => {
  DEBUG_API && console.log('🔄 [API] Bulk updating widget positions:', positions);
  return apiClient.post('/dashboard-builder/widgets/bulk-update-positions/', { positions });
};

export const getChartData = (ticker, range = '1y', indicators = '', indicator_config = null) => {
  const params = { range };

  // Support both legacy indicators param and new indicator_config param
  if (indicator_config) {
    params.indicator_config = indicator_config;
    DEBUG_API && console.log(`📈 [API] Fetching chart data for ${ticker} (${range}, config: ${indicator_config})...`);
  } else if (indicators) {
    params.indicators = indicators;
    DEBUG_API && console.log(`📈 [API] Fetching chart data for ${ticker} (${range}, indicators: ${indicators})...`);
  } else {
    DEBUG_API && console.log(`📈 [API] Fetching chart data for ${ticker} (${range})...`);
  }

  return apiClient.get(`/dashboard-builder/chart-data/${ticker}/`, { params });
};

// Ticker Dashboard System
export const getTickerDashboard = (ticker) => {
  DEBUG_API && console.log(`📊 [API] Fetching ticker dashboard for ${ticker}...`);
  return apiClient.get(`/dashboard-builder/dashboards/ticker/${ticker}/`);
};

export const setTickerTemplate = (dashboardId) => {
  DEBUG_API && console.log(`📍 [API] Setting dashboard ID ${dashboardId} as ticker template...`);
  return apiClient.post(`/dashboard-builder/dashboards/${dashboardId}/set-template/`);
};

// Custom Indicators
export const getCustomIndicators = () => {
  DEBUG_API && console.log('💾 [API] Fetching custom indicators...');
  return apiClient.get('/dashboard-builder/custom-indicators/');
};

export const createCustomIndicator = (data) => {
  DEBUG_API && console.log('💾 [API] Creating custom indicator:', data);
  return apiClient.post('/dashboard-builder/custom-indicators/', data);
};

export const updateCustomIndicator = (id, data) => {
  DEBUG_API && console.log(`💾 [API] Updating custom indicator ID ${id}:`, data);
  return apiClient.put(`/dashboard-builder/custom-indicators/${id}/`, data);
};

export const deleteCustomIndicator = (id) => {
  DEBUG_API && console.log(`💾 [API] Deleting custom indicator ID ${id}...`);
  return apiClient.delete(`/dashboard-builder/custom-indicators/${id}/`);
};

// =============================================================================
// OPTIONS CHART FUNCTIONS
// =============================================================================

/**
 * Fetch historical OHLCV data for a specific options contract
 * @param {string} ticker - Underlying stock symbol
 * @param {string} optionType - 'call' or 'put'
 * @param {number} strike - Strike price
 * @param {string} expiration - Expiration date (YYYY-MM-DD)
 * @param {string} range - Time range for historical data ('1m', '3m', '6m', '1y')
 * @returns {Promise} Response with historical options data
 */
export const getOptionsHistoricalData = async (ticker, optionType, strike, expiration, range = '3m') => {
  DEBUG_API && console.log(`📊 [API] Fetching options historical data for ${ticker} ${optionType} $${strike} ${expiration}...`);
  try {
    const response = await apiClient.get(
      `/dashboard-builder/options-historical/${ticker}/${optionType}/${strike}/${expiration}/?range=${range}`
    );
    return response.data;
  } catch (error) {
    console.error(`❌ [API] Failed to fetch options historical data:`, error);
    throw error;
  }
};

/**
 * Set a dashboard as the options template
 * @param {number} dashboardId - Dashboard ID to set as template
 * @returns {Promise} Response with updated dashboard
 */
export const setOptionsTemplate = async (dashboardId) => {
  DEBUG_API && console.log(`📍 [API] Setting dashboard ID ${dashboardId} as options template...`);
  try {
    const response = await apiClient.post(`/dashboard-builder/dashboards/${dashboardId}/set-options-template/`);
    return response.data;
  } catch (error) {
    console.error(`❌ [API] Failed to set options template:`, error);
    throw error;
  }
};

/**
 * Get options dashboard for a specific contract
 * @param {string} ticker - Underlying stock symbol
 * @param {string} optionType - 'call' or 'put'
 * @param {string} strike - Strike price
 * @param {string} expiration - Expiration date (YYYY-MM-DD)
 * @returns {Promise} Response with dashboard data
 */
export const getOptionsDashboard = async (ticker, optionType, strike, expiration) => {
  DEBUG_API && console.log(`📊 [API] Fetching options dashboard for ${ticker} ${optionType} $${strike} ${expiration}...`);
  try {
    const response = await apiClient.get(
      `/dashboard-builder/dashboards/options/${ticker}/${optionType}/${strike}/${expiration}/`
    );
    return response.data;
  } catch (error) {
    console.error(`❌ [API] Failed to fetch options dashboard:`, error);
    throw error;
  }
};

// =============================================================================
// PASSWORD RESET FUNCTIONS
// =============================================================================

/**
 * Request password reset email
 * @param {string} email - User's email address
 * @returns {Object} Response with message or error
 */
export const requestPasswordReset = async (email) => {
  DEBUG_API && console.log('🔐 [API] Requesting password reset for:', email);
  try {
    const response = await apiClient.post('/auth/request-password-reset/', {
      email
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      return { error: error.response.data.error };
    }
    return { error: 'Failed to send reset email. Please try again.' };
  }
};

/**
 * Validate password reset token
 * @param {string} token - Password reset token
 * @returns {Object} Response with validation result
 */
export const validateResetToken = async (token) => {
  DEBUG_API && console.log('🔐 [API] Validating reset token...');
  try {
    const response = await apiClient.post('/auth/validate-reset-token/', {
      token
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      return { error: error.response.data.error };
    }
    return { error: 'Invalid or expired reset link.' };
  }
};

/**
 * Reset password with token
 * @param {string} token - Password reset token
 * @param {string} newPassword - New password
 * @returns {Object} Response with success message or error
 */
export const resetPassword = async (token, newPassword) => {
  DEBUG_API && console.log('🔐 [API] Resetting password...');
  try {
    const response = await apiClient.post('/auth/reset-password/', {
      token,
      new_password: newPassword
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      return { error: error.response.data.error };
    }
    return { error: 'Failed to reset password. Please try again.' };
  }
};

/**
 * Change password for authenticated user
 * @param {string} currentPassword - User's current password
 * @param {string} newPassword - User's new password
 * @returns {Object} Response with message or error
 */
export const changePassword = async (currentPassword, newPassword) => {
  DEBUG_API && console.log('🔐 [API] Changing password...');
  try {
    const response = await apiClient.post('/auth/change-password/', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      return { error: error.response.data.error };
    }
    return { error: 'Failed to change password. Please try again.' };
  }
};

// =============================================================================
// ACCOUNT DELETION / REINSTATEMENT
// =============================================================================

/**
 * Request account deletion (soft-delete with 90-day retention)
 * @param {string} password - User's current password (or "DELETE" for Google OAuth users)
 * @param {string} reason - Optional reason for leaving
 * @returns {Object} Response with message and permanent_deletion_date, or error
 */
export const deleteAccount = async (password, reason = '') => {
  DEBUG_API && console.log('[API] Requesting account deletion...');
  try {
    const response = await apiClient.post('/auth/delete-account/', {
      password,
      reason
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      return { error: error.response.data.error };
    }
    return { error: 'Failed to delete account. Please try again.' };
  }
};

/**
 * Reinstate a pending account deletion (user self-service)
 * @returns {Object} Response with message or error
 */
export const reinstateAccount = async () => {
  DEBUG_API && console.log('[API] Reinstating account...');
  try {
    const response = await apiClient.post('/auth/reinstate-account/');
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      return { error: error.response.data.error };
    }
    return { error: 'Failed to reinstate account. Please try again.' };
  }
};

// =============================================================================
// FEEDBACK / CONTACT FORM
// =============================================================================

/**
 * Submit feedback/bug report with optional image attachments
 * @param {string} subject - Feedback subject line
 * @param {string} message - Feedback message body
 * @param {File[]} images - Array of image File objects (from clipboard paste or file input)
 * @param {string} replyEmail - Email address for replies
 * @returns {Object} Response with message or error
 */
export const submitFeedback = async (subject, message, images = [], replyEmail = '') => {
  DEBUG_API && console.log('[API] Submitting feedback:', { subject, imageCount: images.length });
  try {
    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('message', message);
    if (replyEmail) {
      formData.append('reply_email', replyEmail);
    }
    images.forEach((img) => {
      formData.append('images', img, img.name || 'screenshot.png');
    });

    const response = await apiClient.post('/auth/feedback/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      return { error: error.response.data.error };
    }
    return { error: 'Failed to send feedback. Please try again.' };
  }
};

// =============================================================================
// USER PROFILE AND ROLE MANAGEMENT FUNCTIONS
// =============================================================================

/**
 * Get current user's profile with subscription and feature access info
 * @returns {Object} User profile with tier and features
 */
export const getUserProfile = async () => {
  DEBUG_API && console.log('👤 [API] Fetching user profile...');
  try {
    const response = await apiClient.get('/auth/profile/');
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch user profile:', error);
    throw error;
  }
};

/**
 * Update current user's profile settings
 * @param {Object} data - Fields to update (e.g., { show_username: false })
 * @returns {Object} Updated user profile
 */
export const updateUserProfile = async (data) => {
  DEBUG_API && console.log('[API] Updating user profile...', data);
  try {
    const response = await apiClient.patch('/auth/profile/', data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to update user profile:', error);
    throw error;
  }
};

/**
 * Get current user's usage statistics
 * @returns {Object} Usage stats with limits and percentages
 */
export const getUsageStats = async () => {
  DEBUG_API && console.log('📊 [API] Fetching usage stats...');
  try {
    const response = await apiClient.get('/auth/usage/');
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch usage stats:', error);
    throw error;
  }
};

/**
 * List all users (admin only)
 * @returns {Array} List of users with tier info
 */
export const adminListUsers = async () => {
  DEBUG_API && console.log('👥 [API] Fetching all users (admin)...');
  try {
    const response = await apiClient.get('/auth/admin/users/');
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch users:', error);
    throw error;
  }
};

/**
 * Get single user details (admin only)
 * @param {number} userId - User ID
 * @returns {Object} User details with tier info
 */
export const adminGetUser = async (userId) => {
  DEBUG_API && console.log(`👤 [API] Fetching user ${userId} (admin)...`);
  try {
    const response = await apiClient.get(`/auth/admin/users/${userId}/`);
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch user:', error);
    throw error;
  }
};

/**
 * Update user's subscription tier (admin only)
 * @param {number} userId - User ID
 * @param {string} tierName - Tier name ('free', 'pro', 'enterprise')
 * @returns {Object} Response with status
 */
export const adminUpdateUserTier = async (userId, tierName) => {
  DEBUG_API && console.log(`🎫 [API] Updating user ${userId} tier to ${tierName}...`);
  try {
    const response = await apiClient.patch(`/auth/admin/users/${userId}/tier/`, {
      tier: tierName
    });
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to update user tier:', error);
    throw error;
  }
};

/**
 * Toggle user's staff status (admin only)
 * @param {number} userId - User ID
 * @param {boolean} isStaff - New staff status
 * @returns {Object} Response with status
 */
export const adminToggleStaff = async (userId, isStaff) => {
  DEBUG_API && console.log(`🔧 [API] Toggling user ${userId} staff status to ${isStaff}...`);
  try {
    const response = await apiClient.patch(`/auth/admin/users/${userId}/staff/`, {
      is_staff: isStaff
    });
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to toggle staff status:', error);
    throw error;
  }
};

/**
 * List all subscription tiers (admin only)
 * @returns {Array} List of tiers
 */
export const adminListTiers = async () => {
  DEBUG_API && console.log('🎫 [API] Fetching tiers (admin)...');
  try {
    const response = await apiClient.get('/auth/admin/tiers/');
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch tiers:', error);
    throw error;
  }
};

/**
 * List all features (admin only)
 * @returns {Array} List of features
 */
export const adminListFeatures = async () => {
  DEBUG_API && console.log('⚙️ [API] Fetching features (admin)...');
  try {
    const response = await apiClient.get('/auth/admin/features/');
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch features:', error);
    throw error;
  }
};

/**
 * Get tier-feature access matrix (admin only)
 * @returns {Object} Matrix with tiers, features, and access configuration
 */
export const adminGetTierMatrix = async () => {
  DEBUG_API && console.log('📊 [API] Fetching tier-feature matrix (admin)...');
  try {
    const response = await apiClient.get('/auth/admin/tier-matrix/');
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch tier matrix:', error);
    throw error;
  }
};

/**
 * Toggle feature access for a tier (admin only)
 * @param {number} tierId - Tier ID
 * @param {number} featureId - Feature ID
 * @param {boolean} isEnabled - Enable or disable
 * @returns {Object} Response with status
 */
export const adminToggleTierFeature = async (tierId, featureId, isEnabled) => {
  DEBUG_API && console.log(`🔀 [API] Toggling tier ${tierId} feature ${featureId} to ${isEnabled}...`);
  try {
    const response = await apiClient.patch('/auth/admin/tier-access/', {
      tier_id: tierId,
      feature_id: featureId,
      is_enabled: isEnabled
    });
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to toggle tier feature:', error);
    throw error;
  }
};

// ============== Tier Limits Management ==============

export const adminGetTierLimits = async () => {
  DEBUG_API && console.log('📊 [API] Fetching tier limits matrix...');
  try {
    const response = await apiClient.get('/auth/admin/tier-limits/');
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch tier limits:', error);
    throw error;
  }
};

export const adminUpdateTierLimit = async (tierId, limitType, limitValue) => {
  DEBUG_API && console.log(`🔧 [API] Updating tier ${tierId} limit ${limitType} to ${limitValue}...`);
  try {
    const response = await apiClient.patch('/auth/admin/tier-limits/update/', {
      tier_id: tierId,
      limit_type: limitType,
      limit_value: limitValue
    });
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to update tier limit:', error);
    throw error;
  }
};

// ============== Stripe Subscription Management ==============

/**
 * Create a Stripe Checkout session for subscribing to a tier
 * @param {string} tier - Tier name ('essential', 'plus', 'premium')
 * @returns {Object} Response with checkout_url to redirect user
 */
export const createCheckoutSession = async (tier) => {
  DEBUG_API && console.log(`💳 [API] Creating checkout session for tier: ${tier}...`);
  try {
    const response = await apiClient.post('/auth/stripe/create-checkout-session/', { tier });
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to create checkout session:', error);
    throw error;
  }
};

/**
 * Create a Stripe Customer Portal session for managing subscription
 * @returns {Object} Response with portal_url to redirect user
 */
export const createPortalSession = async () => {
  DEBUG_API && console.log('💳 [API] Creating portal session...');
  try {
    const response = await apiClient.post('/auth/stripe/create-portal-session/');
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to create portal session:', error);
    throw error;
  }
};

/**
 * Get current subscription status for authenticated user
 * @returns {Object} Subscription status details
 */
export const getSubscriptionStatus = async () => {
  DEBUG_API && console.log('💳 [API] Fetching subscription status...');
  try {
    const response = await apiClient.get('/auth/stripe/subscription-status/');
    return response.data;
  } catch (error) {
    console.error('❌ [API] Failed to fetch subscription status:', error);
    throw error;
  }
};


// ============== Affiliate Program ==============

export const getAffiliateDashboard = async () => {
  const response = await apiClient.get('/auth/affiliate/');
  return response.data;
};

export const setAffiliateCode = async (code) => {
  const response = await apiClient.post('/auth/affiliate/set-code/', { code });
  return response.data;
};

export const createAffiliateCode = async (code, campaignLabel) => {
  const response = await apiClient.post('/auth/affiliate/codes/', { code, campaign_label: campaignLabel });
  return response.data;
};

export const getAffiliateCodeDetail = async (codeId) => {
  const response = await apiClient.get(`/auth/affiliate/codes/${codeId}/`);
  return response.data;
};

export const updateAffiliateCode = async (codeId, data) => {
  const response = await apiClient.patch(`/auth/affiliate/codes/${codeId}/update/`, data);
  return response.data;
};

export const updatePayoutDetails = async (data) => {
  const response = await apiClient.patch('/auth/affiliate/payout-details/', data);
  return response.data;
};

export const applyReferralCode = async (code) => {
  const response = await apiClient.post('/auth/affiliate/apply-code/', { code });
  return response.data;
};

export const getReferralStatus = async () => {
  const response = await apiClient.get('/auth/affiliate/referral-status/');
  return response.data;
};

export const changeReferral = async (code) => {
  const response = await apiClient.post('/auth/affiliate/change-referral/', { code });
  return response.data;
};

export const validateAffiliateCode = async (code) => {
  const response = await apiClient.get(`/auth/affiliate/validate-code/${code}/`);
  return response.data;
};

// ============== Admin Affiliate Payouts ==============

export const getAffiliatePayouts = async () => {
  const response = await adminTestsApiClient.get('/admin-tests/reports/affiliate-payouts/');
  return response.data;
};

export const markCommissionsPaid = async (commissionIds, notes = '') => {
  const response = await adminTestsApiClient.post('/admin-tests/reports/affiliate-payouts/mark-paid/', {
    commission_ids: commissionIds,
    notes,
  });
  return response.data;
};

export const voidCommissions = async (commissionIds, reason = '') => {
  const response = await adminTestsApiClient.post('/admin-tests/reports/affiliate-payouts/void/', {
    commission_ids: commissionIds,
    reason,
  });
  return response.data;
};

export const getAffiliateDetail = async (affiliateId) => {
  const response = await adminTestsApiClient.get(`/admin-tests/reports/affiliate-payouts/${affiliateId}/`);
  return response.data;
};
